import React from "react";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import {
  FiArrowRight,
  FiChevronDown,
  FiMapPin,
  FiPackage,
  FiSun,
} from "react-icons/fi";
import fermatHeroVideo from "../../assets/videos/fermatHero.mp4";
import fermatHeroPoster from "../../assets/videos/fermatHero-poster.jpg";
import { Button } from "../../ui/Button";
import * as D from "./Farms.desktop.styled";

const FarmsHeroDesktop = ({ stats, onExplore }) => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  const container = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: reduceMotion ? 0 : 0.08,
        delayChildren: reduceMotion ? 0 : 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: reduceMotion ? 0 : 16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: reduceMotion ? 0 : 0.6, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const rawTitle = t("farms.hero_title", { defaultValue: "Welcome to Our Farms" });
  const titleWords = rawTitle.trim().split(" ");
  const highlightWord = titleWords[titleWords.length - 1];
  const leadingWords = titleWords.slice(0, -1).join(" ");

  return (
    <D.Hero>
      <D.HeroFrame>
        {/* Decorative. Under prefers-reduced-motion the source is left out entirely, so the
            poster frame shows and the video is never downloaded — the old version autoplayed
            regardless of the preference. */}
        <D.HeroVideo
          key={reduceMotion ? "hero-still" : "hero-motion"}
          poster={fermatHeroPoster}
          autoPlay={!reduceMotion}
          loop={!reduceMotion}
          muted
          playsInline
          preload={reduceMotion ? "none" : "metadata"}
          aria-hidden="true"
          tabIndex={-1}
        >
          {reduceMotion ? null : <source src={fermatHeroVideo} type="video/mp4" />}
        </D.HeroVideo>
        <D.HeroVeil />
        <D.HeroGrain />

        <D.HeroContent
          as={motion.div}
          variants={container}
          initial="hidden"
          animate="visible"
        >
          <motion.span variants={item}>
            <D.HeroEyebrow>
              <FiSun aria-hidden="true" />
              {t("farms.hero_eyebrow", {
                defaultValue: "Local · Seasonal · Direct",
              })}
            </D.HeroEyebrow>
          </motion.span>

          <D.HeroTitle as={motion.h1} variants={item}>
            {leadingWords ? `${leadingWords} ` : ""}
            <em>{highlightWord}</em>
          </D.HeroTitle>

          <D.HeroLede as={motion.p} variants={item}>
            {t("farms.hero_subtitle", {
              defaultValue:
                "Your gateway to fresh, sustainable, and local produce.",
            })}
          </D.HeroLede>

          <D.HeroStats as={motion.div} variants={item}>
            <D.HeroStat>
              <FiPackage aria-hidden="true" />
              <span>
                <strong>{stats.farmCount}</strong>{" "}
                {t("farms.hero_stats.farms", { defaultValue: "local farms" })}
              </span>
            </D.HeroStat>
            <D.HeroStat>
              <FiMapPin aria-hidden="true" />
              <span>
                <strong>{stats.regionCount}</strong>{" "}
                {t("farms.hero_stats.regions", { defaultValue: "regions" })}
              </span>
            </D.HeroStat>
            <D.HeroStat>
              <FiSun aria-hidden="true" />
              <span>
                <strong>{stats.organicCount}</strong>{" "}
                {t("farms.hero_stats.organic", {
                  defaultValue: "organic partners",
                })}
              </span>
            </D.HeroStat>
          </D.HeroStats>

          <D.HeroActions as={motion.div} variants={item}>
            <Button
              type="button"
              variant="primary"
              onClick={onExplore}
              iconRight={<FiArrowRight aria-hidden="true" />}
            >
              {t("farms.hero_cta_primary", {
                defaultValue: "Explore farms",
              })}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                if (typeof window !== "undefined") {
                  window.location.hash = "#how-it-works";
                }
              }}
            >
              {t("farms.hero_cta_secondary", {
                defaultValue: "How it works",
              })}
            </Button>
          </D.HeroActions>
        </D.HeroContent>

        <D.HeroScrollHint>
          {t("farms.hero_scroll", { defaultValue: "Scroll" })}
          <FiChevronDown aria-hidden="true" />
        </D.HeroScrollHint>
      </D.HeroFrame>
    </D.Hero>
  );
};

export default FarmsHeroDesktop;
